import { type FC, useCallback, useRef, useState } from "react";
import type { JsonLdTemplateType, SeoAuditCategory } from "shared";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { Select } from "../components/Select";
import { Tabs } from "../components/Tabs";
import { EmptyPage } from "../layout/EmptyLayout";
import "./SeoAuditPage.css";
import { useWordpress } from "../settings/useIntegrations";
import { useSeoAudit, useSeoAuditActions } from "./useSeoAudit";

const getScoreClass = (score: number): string => {
	if (score >= 70) return "score-good";
	if (score >= 40) return "score-ok";
	return "score-poor";
};

const AuditCategoryCard: FC<{ category: SeoAuditCategory }> = ({
	category,
}) => {
	return (
		<div className="seo-audit-category">
			<div className="seo-audit-category-header">
				<h4 className="seo-audit-category-title">{category.title}</h4>
				<span
					className={`seo-audit-category-score ${getScoreClass(category.score)}`}
				>
					{category.score}/100
				</span>
			</div>

			{category.findings.length > 0 && (
				<div className="seo-audit-category-section">
					<h5>Findings</h5>
					<ul>
						{category.findings.map((finding) => (
							<li key={finding}>{finding}</li>
						))}
					</ul>
				</div>
			)}

			{category.recommendations.length > 0 && (
				<div className="seo-audit-category-section">
					<h5>Recommendations</h5>
					<ul>
						{category.recommendations.map((rec) => (
							<li key={rec}>{rec}</li>
						))}
					</ul>
				</div>
			)}
		</div>
	);
};

export const SeoAuditPage: FC = () => {
	const company = useCompany();
	const { data } = useSeoAudit();
	const { runAudit, generateJsonLd, pushToWordpress } = useSeoAuditActions();
	const { status: wpStatus } = useWordpress();
	const wpConnected = wpStatus.data.connected;
	const [websiteUrl, setWebsiteUrl] = useState(company.website || "");

	const [selectedType, setSelectedType] = useState<JsonLdTemplateType | "">("");
	const [generatedScript, setGeneratedScript] = useState("");
	const [generatedFilename, setGeneratedFilename] = useState("");
	const [copied, setCopied] = useState(false);
	const copiedTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

	const handleGenerate = useCallback(() => {
		if (!selectedType) return;
		generateJsonLd.mutate(selectedType, {
			onSuccess: (data) => {
				setGeneratedScript(data.script);
				setGeneratedFilename(data.filename);
			},
		});
	}, [selectedType, generateJsonLd]);

	const handleCopy = useCallback(() => {
		navigator.clipboard.writeText(generatedScript);
		setCopied(true);
		if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
		copiedTimeout.current = setTimeout(() => setCopied(false), 2000);
	}, [generatedScript]);

	const { audit, monthlyAuditCount, monthlyLimit } = data;
	const canRunAudit = monthlyAuditCount < monthlyLimit;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (websiteUrl && canRunAudit) {
			await runAudit.mutateAsync(websiteUrl);
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	return (
		<EmptyPage title="AI SEO Audit">
			<div className="seo-audit-page">
				<div className="seo-audit-header">
					<div>
						<h1>AI SEO Audit</h1>
						<span className="audit-count">
							{monthlyAuditCount}/{monthlyLimit} audits used this month
						</span>
					</div>
				</div>

				<Tabs defaultActiveKey="audit" className="seo-audit-tabs">
					<Tabs.Panel eventKey="audit" title="AI SEO Audit">
						{!canRunAudit && (
							<div className="seo-audit-limit-warning">
								You have reached your monthly audit limit ({monthlyLimit}
								/month). Your limit will reset at the beginning of next month.
							</div>
						)}

						{!audit ? (
							<div className="seo-audit-form">
								<div className="seo-audit-empty">
									<h2>Run Your First SEO Audit</h2>
									<p>
										Get AI-powered insights about your website's SEO
										performance. Enter your website URL below to start.
									</p>
								</div>
								<form onSubmit={handleSubmit}>
									<div className="seo-audit-form-input">
										<input
											type="url"
											value={websiteUrl}
											onChange={(e) => setWebsiteUrl(e.target.value)}
											placeholder="https://example.com"
											required
											disabled={!canRunAudit}
										/>
										<Button
											type="submit"
											loading={runAudit.isPending}
											disabled={!canRunAudit || !websiteUrl}
										>
											Run Audit
										</Button>
									</div>
								</form>
							</div>
						) : (
							<>
								<div className="seo-audit-form">
									<h2>Run New Audit</h2>
									<p>
										Enter a website URL to run a new SEO audit. You have{" "}
										{monthlyLimit - monthlyAuditCount} audit(s) remaining this
										month.
									</p>
									<form onSubmit={handleSubmit}>
										<div className="seo-audit-form-input">
											<input
												type="url"
												value={websiteUrl}
												onChange={(e) => setWebsiteUrl(e.target.value)}
												placeholder="https://example.com"
												required
												disabled={!canRunAudit}
											/>
											<Button
												type="submit"
												loading={runAudit.isPending}
												disabled={!canRunAudit || !websiteUrl}
											>
												Run Audit
											</Button>
										</div>
									</form>
								</div>

								<div className="seo-audit-results">
									<div className="seo-audit-meta">
										<span className="seo-audit-meta-item">
											Website: {audit.websiteUrl}
										</span>
										<span className="seo-audit-meta-item">
											Generated: {formatDate(audit.auditResults.generatedAt)}
										</span>
									</div>

									<div className="seo-audit-score-section">
										<div className="seo-audit-score">
											<span
												className={`seo-audit-score-value ${getScoreClass(audit.auditResults.overallScore)}`}
											>
												{audit.auditResults.overallScore}
											</span>
											<span className="seo-audit-score-label">
												Overall Score
											</span>
										</div>
										<div className="seo-audit-summary">
											<h3>Summary</h3>
											<p>{audit.auditResults.summary}</p>
										</div>
									</div>

									<div className="seo-audit-categories">
										{audit.auditResults.categories.map((category) => (
											<AuditCategoryCard
												key={category.title}
												category={category}
											/>
										))}
									</div>
								</div>
							</>
						)}
					</Tabs.Panel>

					<Tabs.Panel eventKey="json-schemas" title="JSON Schemas">
						<div className="json-schema-tab">
							<div className="json-schema-intro">
								<h2>Structured Data & Configuration Files</h2>
								<p>
									Improve how search engines and AI assistants understand your
									business by adding structured data to your website. Generate
									JSON-LD schemas, crawler directives, and AI context files
									tailored to your company.
								</p>
								<div className="json-schema-info-cards">
									<div className="json-schema-info-card">
										<h4>JSON-LD Schemas</h4>
										<p>
											Structured data markup that helps Google, Bing, and other
											search engines display rich results — star ratings,
											business details, and more — directly in search listings.
											FAQ schema no longer produces Google rich results (retired
											May 2026) but still gives AI assistants structured FAQ
											content.
										</p>
									</div>
									<div className="json-schema-info-card">
										<h4>robots.txt</h4>
										<p>
											A configuration file placed at your site root that tells
											search engine crawlers which pages to index and which to
											skip. Essential for controlling crawler access and
											preserving crawl budget.
										</p>
									</div>
									<div className="json-schema-info-card">
										<h4>llm.txt</h4>
										<p>
											A context file describing your business, services, and key
											information for AI assistants. Google Search ignores it
											and major AI search engines haven't confirmed reading it
											yet — a zero-cost extra, not a ranking lever.
										</p>
									</div>
								</div>
							</div>

							<div className="json-schema-wp-plugin">
								<p>
									Using WordPress?{" "}
									<a href="/api/seo-audit/wordpress-plugin" download>
										Download the Firestarter SEO plugin
									</a>{" "}
									to automatically inject schemas, robots.txt, and llm.txt into
									your site.
								</p>
							</div>

							<div className="json-schema-generator">
								<h3>Generate a Schema or File</h3>
								<div className="json-schema-controls">
									<Select
										value={selectedType}
										onChange={(e) =>
											setSelectedType(e.target.value as JsonLdTemplateType | "")
										}
									>
										<option value="">Select a type...</option>
										<option value="LocalBusiness">Site Wide Base Graph</option>
										<option value="Service">Service Page</option>
										<option value="FAQPage">FAQ schema (AI context)</option>
										<option value="SocialProof">Social Proof</option>
										<option value="Speakable">Speakable</option>
										<option value="RobotsTxt">robots.txt</option>
										<option value="LlmTxt">llm.txt</option>
									</Select>
									<Button
										onClick={handleGenerate}
										loading={generateJsonLd.isPending}
										disabled={!selectedType || generateJsonLd.isPending}
									>
										Generate
									</Button>
								</div>
							</div>

							{generatedScript && (
								<div className="json-schema-editor">
									<div className="json-schema-editor-header">
										<span>{generatedFilename}</span>
										<div className="json-schema-editor-actions">
											<Button onClick={handleCopy}>
												{copied ? "Copied!" : "Copy"}
											</Button>
											<Button
												onClick={() => {
													if (selectedType) {
														pushToWordpress.mutate({
															templateType: selectedType,
															script: generatedScript,
														});
													}
												}}
												loading={pushToWordpress.isPending}
												disabled={
													!wpConnected ||
													!generatedScript ||
													pushToWordpress.isPending
												}
											>
												Push to WordPress
											</Button>
										</div>
									</div>
									<textarea
										value={generatedScript}
										onChange={(e) => setGeneratedScript(e.target.value)}
										rows={20}
										spellCheck={false}
										className="json-schema-code"
									/>
								</div>
							)}
						</div>
					</Tabs.Panel>
				</Tabs>
			</div>
		</EmptyPage>
	);
};
